<template>
    <BaseDialog
        v-model:open="open"
        title="Review last week's actions"
        description="Record the outcome for each action from last week before planning this week."
        disable-close-on-backdrop-click
        dialog-content-class="!top-4 !right-4 !bottom-4 !left-4 !w-auto !max-h-none !max-w-[38.75rem] !overflow-hidden !rounded-xl !p-0 !pb-0 md:!top-1/2 md:!right-auto md:!bottom-auto md:!left-1/2 md:!w-[calc(100%-2rem)] md:!max-h-[80dvh]">
        <section data-testid="weekly-action-review" class="flex h-full min-h-0 flex-col overflow-hidden md:max-h-[80dvh]">
            <header class="journeys-review-dialog-header flex shrink-0 items-center gap-3 border-b border-[#E7E2DB] px-5 py-5 sm:px-6">
                <svg width="28" height="28" viewBox="0 0 22 22.0022" fill="none" aria-hidden="true">
                    <path
                        d="M22 21.9985C21.9377 18.7795 20.8083 16.1323 18.612 14.0535C16.4817 12.0407 13.948 11.0214 11.011 11.0031C4.92433 10.9591 0.00366667 15.938 0 22.0022H22V21.9985Z"
                        fill="#30A29D" />
                    <path
                        d="M0 5.85606e-05C0.0623333 3.21912 1.19167 5.86624 3.388 7.94506C5.51834 9.9579 8.052 10.9771 10.989 10.9955C17.0757 11.0431 21.9963 6.06422 22 5.85606e-05H0Z"
                        fill="#81DADA" />
                </svg>
                <div class="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                    <h2 class="journeys-review-dialog-header__title">First — how did last week land?</h2>
                    <span class="journeys-review-dialog-header__range">{{ weekRange }}</span>
                </div>
            </header>

            <div class="min-h-0 flex-1 overflow-y-auto bg-[#F7F5F2]">
                <div class="journeys-review-action-list flex flex-col" aria-label="Actions from last week">
                    <section v-if="completedActionGroups.length > 0" class="journeys-review-completed-section flex flex-col" aria-labelledby="review-completed-actions-title">
                        <h3 id="review-completed-actions-title" class="journeys-review-completed-section__title">Completed</h3>
                        <div v-for="group in completedActionGroups" :key="group.key" class="flex flex-col gap-2.5">
                            <h4 class="journeys-review-completed-section__group flex items-center gap-1">
                                <span class="size-2 rounded-full bg-[#81DADA]" aria-hidden="true"></span>
                                {{ group.title }}
                            </h4>
                            <ul class="flex flex-col gap-2.5">
                                <li v-for="action in group.actions" :key="action.id">
                                    <WeeklyActionCard
                                        :action="action"
                                        :role-play-setup-url="rolePlaySetupUrl"
                                        variant="review"
                                        :review-outcome="outcomes[action.id]"
                                        :timezone="timezone"
                                        :review-disabled="isSubmitting"
                                        @set-review-outcome="setOutcome"
                                        @view-details="(actionId) => emit('view-details', actionId)" />
                                </li>
                            </ul>
                        </div>
                    </section>

                    <section
                        v-for="group in activeActionGroups"
                        :key="group.key"
                        class="journeys-review-action-group flex flex-col"
                        :aria-labelledby="`review-action-group-${group.key}`">
                        <h4 :id="`review-action-group-${group.key}`" class="journeys-review-action-group__label flex items-center gap-1">
                            <span class="size-2 rounded-full bg-[#81DADA]" aria-hidden="true"></span>
                            {{ group.title }}
                        </h4>
                        <ul class="flex flex-col gap-2.5">
                            <li v-for="action in group.actions" :key="action.id">
                                <WeeklyActionCard
                                    :action="action"
                                    :role-play-setup-url="rolePlaySetupUrl"
                                    variant="review"
                                    :review-outcome="outcomes[action.id]"
                                    :timezone="timezone"
                                    :review-disabled="isSubmitting"
                                    @set-review-outcome="setOutcome"
                                    @view-details="(actionId) => emit('view-details', actionId)" />
                            </li>
                        </ul>
                    </section>
                </div>
            </div>

            <footer class="flex shrink-0 justify-end bg-[#F7F5F2] px-5 pb-5 pt-3 sm:px-6 sm:pb-6">
                <button
                    data-testid="continue-weekly-action-review"
                    type="button"
                    class="inline-flex min-h-10 items-center justify-center rounded-lg bg-[#3A3631] px-4 py-2 text-sm leading-5 font-medium text-white transition-colors hover:bg-[#2A2723] disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="isSubmitting"
                    :aria-busy="isSubmitting"
                    @click="submitReview">
                    {{ isSubmitting ? "Saving…" : "Save and plan this week" }}
                </button>
            </footer>
        </section>
    </BaseDialog>
</template>

<script setup lang="ts">
import type { DevelopmentActionReviewOutcome } from "~vue/api/generated/development-journeys";
import BaseDialog from "~vue/components/BaseDialog.vue";
import { computed, ref, watch } from "vue";

import WeeklyActionCard from "./WeeklyActionCard.vue";
import type { WeeklyAction } from "./weeklyActions";

const props = defineProps<{
    actions: WeeklyAction[];
    rolePlaySetupUrl: string;
    weekRange: string;
    timezone?: string;
    isSubmitting: boolean;
}>();

const open = defineModel<boolean>("open", { default: false });

const emit = defineEmits<{
    submit: [outcomes: DevelopmentActionReviewOutcome[]];
    "view-details": [actionId: string];
    "update:reviewed-action-count": [count: number];
}>();

type ReviewOutcome = DevelopmentActionReviewOutcome["status"];
type ActionGroup = { key: string; title: string; actions: WeeklyAction[] };

const outcomes = ref<Record<string, ReviewOutcome>>({});
const reviewedActionCount = computed(() => Object.keys(outcomes.value).length);
const activeActionGroups = computed<ActionGroup[]>(() => groupActions(props.actions.filter((action) => outcomes.value[action.id] !== "completed")));
const completedActionGroups = computed<ActionGroup[]>(() => groupActions(props.actions.filter((action) => outcomes.value[action.id] === "completed")));

watch(
    () => props.actions.map((action) => `${action.id}:${action.status}`).join(","),
    () => {
        outcomes.value = Object.fromEntries(props.actions.filter((action) => action.status === "completed").map((action) => [action.id, "completed"]));
    },
    { immediate: true },
);

watch(reviewedActionCount, (count) => emit("update:reviewed-action-count", count), { immediate: true });

const setOutcome = (actionId: string, outcome: ReviewOutcome) => {
    if (props.isSubmitting) {
        return;
    }
    outcomes.value = { ...outcomes.value, [actionId]: outcome };
};

const submitReview = () => {
    if (props.isSubmitting) {
        return;
    }

    emit(
        "submit",
        props.actions.map((action) => ({ action_id: action.id, status: outcomes.value[action.id] ?? "not_completed" })),
    );
};

function groupActions(actions: WeeklyAction[]): ActionGroup[] {
    const groups = new Map<string, ActionGroup>();
    for (const action of actions) {
        const goal = action.development_goal;
        const key = goal?.id ?? "independent-actions";
        const title = goal?.title ?? "Actions";
        const group = groups.get(key) ?? { key, title, actions: [] };
        group.actions.push(action);
        groups.set(key, group);
    }
    return [...groups.values()];
}
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

.journeys-review-action-list {
    gap: 20px;
    padding: 20px;
}

.journeys-review-dialog-header__title {
    color: #2a2723;
    font-family: var(--font-serif, Georgia, serif);
    font-size: 21px;
    font-weight: 500;
    line-height: 28px;
}

.journeys-review-dialog-header__range {
    color: #6e6860;
    font-family: var(--font-app, Inter, sans-serif);
    font-size: 14px;
    line-height: 20px;
}

.journeys-review-action-group {
    gap: 3px;
}

.journeys-review-action-group + .journeys-review-action-group {
    margin-top: 20px;
}

.journeys-review-action-group__label,
.journeys-review-completed-section__title,
.journeys-review-completed-section__group {
    color: #6e6860;
    font-family: var(--font-scaffold, "Space Grotesk", sans-serif);
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
}

.journeys-review-completed-section {
    gap: 10px;
}
</style>
